import { ChevronRight } from 'lucide-react';
import { Children, type ReactNode, useState } from 'react';
import { type FieldValues } from 'react-hook-form';

import { FieldFullScreenDialog } from './FieldFullScreenDialog';
import {
  type ListFiltersPhone,
  ListResultsPhone,
  type ListResultsPhoneProps,
  ListSearchPhone,
} from './ListPhone';
import { ListRows } from './ListRow';

/** How many rows a record shows before "All …". */
export const RECORD_LIST_PREVIEW = 5;

type ResultsProps = Omit<ListResultsPhoneProps, 'children' | 'showCount' | 'variant'>;

/**
 * A list inside a record on phones and tablets (a customer's properties, a property's jobs,
 * documents, activity): the heading with its count and its one add action, one search box with
 * Filter (or Sort) inside it, the applied filters as removable chips, then the rows, loading more
 * as the list scrolls. The same shape as the search screens, so every list reads alike.
 */
export const RecordListPhone = <TFilters extends FieldValues>({
  title,
  count,
  addAction,
  search,
  filters,
  results,
  preview,
  inCard = false,
  footer,
  children,
}: {
  /** The card's heading, as desktop words it: "Properties", "Jobs". */
  title: string;
  /** Shown after the heading as desktop shows it, "(3)"; left out while loading. */
  count?: ReactNode;
  addAction?: ReactNode;
  search?: { value: string; onChange: (value: string) => void; placeholder: string };
  filters?: ListFiltersPhone<TFilters>;
  results: ResultsProps;
  /**
   * The record shows a short preview and the whole list opens on a screen of its own, so a long
   * list never pushes the record's other sections down. Without it, the list is shown in full.
   */
  preview?: {
    /** What the record shows. Left out, it's the first five rows of the list. */
    rows?: ReactNode;
    /** Whether there's more than the preview shows, so "All …" is worth a row. */
    hasMore: boolean;
    /** "All 16 jobs". */
    allLabel: string;
    /** Back on the record: clear the search and filters so the preview is the plain list. */
    onCloseAll?: () => void;
  };
  /**
   * The heading inside the card with the rows, for a dashboard whose other cards hold their own
   * titles. Records keep the heading above the card. Needs `preview`.
   */
  inCard?: boolean;
  /** Under the rows, inside the card (with `inCard`): a caption, or a link row. */
  footer?: ReactNode;
  children: ReactNode;
}) => {
  const [isAllOpen, setIsAllOpen] = useState(false);

  const heading = (
    <div className="flex min-h-11 items-center justify-between gap-2">
      <h2 className="text-lg font-semibold">
        {title}
        {count !== undefined && (
          <span className="text-muted-foreground ml-1.5 text-sm font-normal tabular-nums">
            {count}
          </span>
        )}
      </h2>
      {addAction}
    </div>
  );

  const fullList = (
    <>
      <ListSearchPhone search={search} filters={filters} />
      <ListResultsPhone {...results} showCount={false}>
        {children}
      </ListResultsPhone>
    </>
  );

  if (!preview) {
    return (
      <div className="flex flex-col gap-3">
        {heading}
        {fullList}
      </div>
    );
  }

  const closeAll = (open: boolean) => {
    setIsAllOpen(open);
    if (!open) preview.onCloseAll?.();
  };

  const allDialog = (
    <FieldFullScreenDialog open={isAllOpen} onOpenChange={closeAll} title={title} closeLabel="Back">
      <div className="flex flex-col gap-3">{fullList}</div>
    </FieldFullScreenDialog>
  );
  const allRow = preview.hasMore && (
    <button
      type="button"
      onClick={() => setIsAllOpen(true)}
      className="active:bg-muted border-border flex min-h-12 w-full items-center justify-between gap-3 border-t px-4 text-left text-sm font-semibold"
    >
      {preview.allLabel}
      <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden />
    </button>
  );
  const previewRows = preview.rows ?? Children.toArray(children).slice(0, RECORD_LIST_PREVIEW);
  // The preview has no paging of its own: the rows it shows, the "All …" row, and the footer.
  const previewResults = {
    ...results,
    hasNextPage: false,
    isFetchingNextPage: false,
    isNextPageError: false,
    loadedCount: 0,
    loadingRows: 3,
  };

  if (inCard) {
    return (
      <div className="bg-card border-border overflow-hidden rounded-lg border">
        <div className="px-4 pt-2">{heading}</div>
        <ListResultsPhone
          {...previewResults}
          showCount={false}
          variant="inCard"
          rowsAsGiven
          footer={allRow}
        >
          <ListRows label={title}>{previewRows}</ListRows>
        </ListResultsPhone>
        {footer && <div className="border-border border-t">{footer}</div>}
        {allDialog}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {heading}
      <ListResultsPhone {...previewResults} showCount={false} rowsAsGiven footer={allRow}>
        <ListRows label={title}>{previewRows}</ListRows>
      </ListResultsPhone>
      {allDialog}
    </div>
  );
};
